import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IModuleDefinition, IRecordListModel, IRecordTypeDefinition, ViewKey } from '@platform/core/types';
import type { IMenuEntry } from '@platform/engine/menu/IMenuEntry';
import { useAdvancedDsl } from '@platform/engine/list/advanced/useAdvancedDsl';
import { useAdvancedFilter } from '@platform/engine/list/advanced/useAdvancedFilter';
import { useQuickFields } from '@platform/engine/list/advanced/useQuickFields';
import { newRecordPreset } from '@platform/engine/list/create/newRecordPreset';
import { emptyStateOf } from '@platform/engine/list/emptyState';
import { hasActiveFilters, RESET_FILTERS } from '@platform/engine/list/filters/activeFilters';
import { ignoresMenuFilter } from '@platform/engine/list/listHeading';
import { useSavedView } from '@platform/engine/list/savedViews/useSavedView';
import { useViewSettings } from '@platform/engine/list/savedViews/useViewSettings';
import { useCapability } from '@platform/engine/list/toolbar/useCapability';
import { useListActions } from '@platform/engine/list/toolbar/useListActions';
import { useRecordListModel } from '@platform/engine/list/useRecordListModel';
import { useAppDispatch } from '@platform/state/hooks';
import { dialogOpened } from '@platform/state/slices/uiSlice';
import { kindField } from './designSections';
import type { IDesignList } from './IDesignList';

/**
 * Список записів бізнес-аналізу на сторінці «Проєктування» модуля (прототип v4 dsgGo → navGo("ba", …, "table")): модель списку з фільтром
 * вкладки (`entry.filter`), подання перемикаються на місці (без переходу до модуля аналізу), дії панелі, створення з наперед заданими
 * видом, модулем і MoSCoW (`preset`) або вибраним видом (`createKind` — меню «+ Створити ▾»), «Налаштування подання» й порожній стан
 * зі скиданням фільтрів або створенням. Колонки — лише ті, що показує розділ (`showColumn`: без модуля, виду й полів інших видів).
 */
export function useDesignList(
  ws: string, module: IModuleDefinition, rt: IRecordTypeDefinition, entry: IMenuEntry, preset: Record<string, string>, createText: string,
  showColumn: (field: string) => boolean = () => true,
): IDesignList {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const base = useRecordListModel(ws, module, rt, entry, undefined, { skipPage: ignoresMenuFilter });
  const columns = useMemo(() => base.columns.filter(showColumn), [base.columns, showColumn]);
  const model: IRecordListModel = { ...base, columns, openView: (view: ViewKey) => base.setState({ view }) };
  const saved = useSavedView(ws, rt.key, model.state.savedViewId)?.definition;
  const { actions: listActions, confirm } = useListActions(model, entry, saved);
  const createWith = (values: Record<string, string>) => {
    newRecordPreset.set(values);
    dispatch(dialogOpened({ kind: 'newRecord', recordType: rt.key }));
  };
  const create = () => createWith(preset);
  const actions = { ...listActions, create };
  const settings = useViewSettings(model);
  const canCreate = useCapability(ws, 'edit');
  const advanced = useAdvancedDsl(ws, rt.key);
  const [, setConditions] = useAdvancedFilter(ws, rt.key);
  const [, setQuick] = useQuickFields(ws, rt.key);
  const reset = () => { model.setState(RESET_FILTERS); setConditions([]); setQuick({}); };
  const state = emptyStateOf({ narrowed: hasActiveFilters(model.state) || Boolean(advanced), filtered: false, item: '', create: createText, canCreate }, t);
  const empty = { text: state.text, actionText: state.actionText, onAction: state.action === 'reset' ? reset : state.action === 'create' ? create : undefined };
  return { model, actions, confirm, settings, empty, createKind: (kind: string) => createWith({ ...preset, [kindField]: kind }) };
}
